import React, { Component } from "react";
import axios from "../../utils/axiosInstance";
import Swal from "sweetalert2";

class Investors extends Component {

  state = {
    investors: [],
    formData: {
      id: null,
      investor_title: "",
      investor_description: "",
      investor_file: null,
      investor_status: "1",
      investor_date: "",
    },
    deleteId: null,
    error: "",
    loading: false,
    submitting: false,
    modalMode: "add",
    selectedFileName: "",
  };

  componentDidMount() {
    this.fetchInvestors();

    const modalEl = document.getElementById("investorModal");
    if (modalEl) {
      modalEl.addEventListener("hidden.bs.modal", () => {
        this.resetForm();
      });
    }
  }

  /** ✅ Fetch all investors */
  fetchInvestors = async () => {
    this.setState({ loading: true, error: "" });
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get("/investors", {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = res.data?.data || [];
      this.setState({ investors: Array.isArray(data) ? data : [] });
    } catch (err) {
      this.setState({
        error:
          err.response?.data?.message ||
          "Failed to load investors. Please try again.",
      });
    } finally {
      this.setState({ loading: false });
    }
  };

  /** ✅ Reset form */
  resetForm = () => {
    this.setState({
      formData: {
        id: null,
        investor_title: "",
        investor_description: "",
        investor_file: null,
        investor_status: "1",
        investor_date: "",
      },
      selectedFileName: "",
      error: "",
      submitting: false,
      modalMode: "add",
    });
  };

  /** ✅ Input change */
  handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "investor_file") {
      this.setState((prev) => ({
        formData: {
          ...prev.formData,
          investor_file: files[0],
        },
        selectedFileName: files[0] ? files[0].name : "",
      }));
    } else {
      this.setState((prev) => ({
        formData: {
          ...prev.formData,
          [name]: value,
        },
      }));
    }
  };

  /** ✅ Open modal */
  openModal = (mode = "add", item = null) => {
    const modal = new window.bootstrap.Modal(
      document.getElementById("investorModal")
    );

    if (mode === "edit" && item) {
      this.setState({
        modalMode: "edit",
        formData: {
          id: item.id,
          investor_title: item.investor_title || "",
          investor_description: item.investor_description || "",
          investor_file: null,
          investor_status: item.investor_status?.toString() ?? "1",
          investor_date: item.investor_date || "",
        },
        selectedFileName: item.investor_file || "",
        error: "",
      });
    } else {
      this.resetForm();
    }

    modal.show();
  };

  /** ✅ Submit add/update */
  handleSubmit = async (e) => {
    e.preventDefault();
    const { formData, modalMode } = this.state;

    if (!formData.investor_title) {
      return this.setState({ error: "Investor title is required." });
    }

    const data = new FormData();
    data.append("investor_title", formData.investor_title);
    data.append("investor_description", formData.investor_description);
    data.append("investor_status", formData.investor_status);
    data.append("investor_date", formData.investor_date);

    if (formData.investor_file) {
      data.append("investor_file", formData.investor_file);
    }

    this.setState({ submitting: true, error: "" });

    try {
      const token = localStorage.getItem("token");

      if (modalMode === "add") {
        await axios.post("/investors", data, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
      } else {
        await axios.post(`/investors/${formData.id}?_method=PUT`, data, {
          headers: {
            "Content-Type": "multipart/form-data",
            Authorization: `Bearer ${token}`,
          },
        });
      }

      await this.fetchInvestors();

      const modal = window.bootstrap.Modal.getInstance(
        document.getElementById("investorModal")
      );
      if (modal) modal.hide();

      Swal.fire({
        icon: "success",
        title:
          modalMode === "add"
            ? "Investor added successfully!"
            : "Investor updated successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      console.error(err);
      this.setState({
        error:
          err.response?.data?.message ||
          err.response?.data?.error ||
          "Error saving investor.",
      });
    } finally {
      this.setState({ submitting: false });
    }
  };

  /** ✅ Confirm delete */
  confirmDelete = (id) => {
    this.setState({ deleteId: id });
    const modal = new window.bootstrap.Modal(
      document.getElementById("deleteModal")
    );
    modal.show();
  };

  /** ✅ Delete investor */
  handleDelete = async () => {
    const { deleteId } = this.state;
    if (!deleteId) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(`/investors/${deleteId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      await this.fetchInvestors();

      const modal = window.bootstrap.Modal.getInstance(
        document.getElementById("deleteModal")
      );
      if (modal) modal.hide();

      Swal.fire({
        icon: "success",
        title: "Investor deleted successfully!",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      console.error(err);
      Swal.fire({
        icon: "error",
        title: "Failed to delete investor.",
      });
    }
  };

  render() {
    const {
      investors,
      formData,
      error,
      loading,
      submitting,
      modalMode,
      selectedFileName,
    } = this.state;
    const baseUrl = axios.defaults.baseURL.replace("/api", "");
    return (
      <div className="pc-container">
        <div className="pc-content">
          <div className="row">
            <div className="col-lg-12">
              <div className="card shadow-none">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <ul className="breadcrumb mb-0">
                    <li className="breadcrumb-item">Investors</li>
                    <li className="breadcrumb-item active">List</li>
                  </ul>

                  <button
                    type="button"
                    className="btn btn-light-warning"
                    onClick={() => this.openModal("add")}
                  >
                    + New Investor
                  </button>
                </div>

                <div className="card-body">
                  {error && <div className="alert alert-danger">{error}</div>}

                  {loading ? (
                    <div className="text-center py-4">Loading...</div>
                  ) : (
                    <div className="table-responsive">
                      <table className="table table-bordered table-striped">
                        <thead>
                          <tr>
                            <th>#</th>
                            <th>Title</th>
                            <th>Description</th>
                            <th>File</th>
                            <th>Status</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {investors.length > 0 ? (
                            investors.map((item, index) => (
                              <tr key={item.id}>
                                <td>{index + 1}</td>
                                <td>{item.investor_title}</td>
                                <td>
                                  {item.investor_description
                                    ? item.investor_description.length > 80
                                      ? item.investor_description.substring(0, 80) + "..."
                                      : item.investor_description
                                    : "N/A"}
                                </td>
                                <td>

                                   {item.investor_file ? (
                                    <img
                                      src={`${baseUrl}/uploads/investors/${item.investor_file}`}
                                      alt={item.investor_title}
                                      style={{
                                        width: "60px",
                                        height: "60px",
                                        objectFit: "cover",
                                        borderRadius: "6px",
                                      }}
                                    />
                                  ) : (
                                    "N/A"
                                  )}


                                </td>
                                <td>
                                  {item.investor_status === "1" || item.investor_status === 1 ? (
                                    <span className="badge bg-success">Active</span>
                                  ) : (
                                    <span className="badge bg-danger">Inactive</span>
                                  )}
                                </td>
                                <td>
                                  <button
                                    className="btn btn-sm btn-light-success me-2"
                                    onClick={() => this.openModal("edit", item)}
                                  >
                                    <i className="ph ph-note-pencil"></i>
                                  </button>

                                  <button
                                    className="btn btn-sm btn-light-danger"
                                    onClick={() => this.confirmDelete(item.id)}
                                  >
                                    <i className="ph ph-trash"></i>
                                  </button>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="6" className="text-center">
                                No investors found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Add / Edit Modal */}
        <div className="modal fade" id="investorModal" tabIndex={-1}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <form onSubmit={this.handleSubmit}>
                <div className="modal-header">
                  <h5 className="modal-title">
                    {modalMode === "add" ? "Add Investor" : "Edit Investor"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    data-bs-dismiss="modal"
                  ></button>
                </div>

                <div className="modal-body">
                  <div className="row">
                    {/* Investor Status */}
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Status</label>
                      <select
                        name="investor_status"
                        className="form-select"
                        value={formData.investor_status}
                        onChange={this.handleChange}
                        required
                      >
                        <option value="1">Active</option>
                        <option value="0">Inactive</option>
                      </select>
                    </div>

                    {/* Investor Title */}
                    <div className="col-md-12 mb-3">
                      <label className="form-label">Investor Title</label>
                      <input
                        type="text"
                        name="investor_title"
                        className="form-control"
                        value={formData.investor_title}
                        onChange={this.handleChange}
                        required
                      />
                    </div>

                    {/* Investor File */}
                    <div className="col-md-12 mb-3">
                      <label className="form-label">Investor File</label>
                      <input
                        type="file"
                        name="investor_file"
                        className="form-control"
                        onChange={this.handleChange}
                      />
                      {selectedFileName && (
                        <small className="text-muted d-block mt-2">
                          Current: {selectedFileName}
                        </small>
                      )}
                    </div>

                    {/* Investor Description */}
                    <div className="col-md-12 mb-3">
                      <label className="form-label">Investor Description</label>
                      <textarea
                        name="investor_description"
                        className="form-control"
                        rows="6"
                        value={formData.investor_description}
                        onChange={this.handleChange}
                      ></textarea>
                    </div>

                    {error && (
                      <div className="col-md-12">
                        <div className="alert alert-danger">{error}</div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-light-danger"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    className="btn btn-light-primary"
                    disabled={submitting}
                  >
                    {submitting
                      ? "Saving..."
                      : modalMode === "add"
                      ? "Add"
                      : "Update"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {/* Delete Modal */}
        <div className="modal fade" id="deleteModal" tabIndex={-1}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-danger">
              <div className="modal-body text-center p-4">
                <i className="ph ph-warning text-danger f-40 mb-3"></i>
                <h5>Are you sure?</h5>
                <p className="text-muted mb-4">This action cannot be undone.</p>

                <div className="d-flex justify-content-center gap-3">
                  <button
                    type="button"
                    className="btn btn-light-secondary"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>

                  <button
                    type="button"
                    className="btn btn-light-danger"
                    onClick={this.handleDelete}
                  >
                    Yes, Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default Investors;

